import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { FormGroup, Input, Select, TextArea, Button } from '../../styles/components';
import { theme } from '../../styles/theme';
import { BAHRAIN_CITIES, PAYMENT_METHODS } from '../../utils/types';
import { useCart } from '../../context/CartContext';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const FormSection = styled.div`
  background-color: ${theme.colors.background};
  border-radius: ${theme.borderRadius.medium};
  padding: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.lg};
  box-shadow: ${theme.shadows.small};
`;

const SectionTitle = styled.h3`
  font-size: 1.25rem;
  margin-bottom: ${theme.spacing.md};
  padding-bottom: ${theme.spacing.sm};
  border-bottom: 1px solid ${theme.colors.lightGray};
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.md};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const PaymentMethodsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const PaymentMethod = styled.label`
  display: flex;
  align-items: center;
  padding: ${theme.spacing.sm};
  border: 1px solid ${theme.colors.gray};
  border-radius: ${theme.borderRadius.small};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: ${theme.colors.primary};
  }
  
  &.selected {
    border-color: ${theme.colors.primary};
    background-color: rgba(255, 215, 0, 0.1);
  }
  
  input {
    margin-right: ${theme.spacing.sm};
  }
`;

const TermsCheckbox = styled.div`
  display: flex;
  align-items: center;
  margin: ${theme.spacing.md} 0;
  
  input {
    margin-right: ${theme.spacing.sm};
  }
  
  a {
    color: ${theme.colors.primary};
    text-decoration: underline;
    
    &:hover {
      color: ${theme.colors.secondary};
    }
  }
`;

const SubmitButton = styled(Button)`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  font-size: 1.125rem;
  margin-top: ${theme.spacing.md};
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const CheckoutForm: React.FC = () => {
  const navigate = useNavigate();
  const { clearCart } = useCart();
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    paymentMethod: 'card',
    notes: '',
    agreeToTerms: false
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };
  
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData({ ...formData, [name]: checked });
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };
  
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    // Required fields
    if (!formData.firstName) newErrors.firstName = 'First name is required';
    if (!formData.lastName) newErrors.lastName = 'Last name is required';
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.phone) newErrors.phone = 'Phone number is required';
    if (!formData.address) newErrors.address = 'Address is required';
    if (!formData.city) newErrors.city = 'City is required';
    if (!formData.postalCode) newErrors.postalCode = 'Postal code is required';
    if (!formData.agreeToTerms) newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    
    // Email validation
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Phone validation (simple check for Bahrain format)
    if (formData.phone && !/^(\+973|973)?\d{8}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid Bahrain phone number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Here you would normally submit the form to your backend
      console.log('Form submitted successfully', formData);
      
      // Simulate successful order
      clearCart();
      navigate('/order-success');
    } else {
      console.log('Form validation failed');
    }
  };
  
  return (
    <Form onSubmit={handleSubmit}>
      <FormSection>
        <SectionTitle>Personal Information</SectionTitle>
        <FieldRow>
          <FormGroup>
            <label htmlFor="firstName">First Name*</label>
            <Input 
              type="text" 
              id="firstName" 
              name="firstName" 
              value={formData.firstName} 
              onChange={handleChange}
              placeholder="John"
              className={errors.firstName ? 'error' : ''}
            />
            {errors.firstName && <div className="error-message">{errors.firstName}</div>}
          </FormGroup>
          
          <FormGroup>
            <label htmlFor="lastName">Last Name*</label>
            <Input 
              type="text" 
              id="lastName" 
              name="lastName" 
              value={formData.lastName} 
              onChange={handleChange}
              placeholder="Doe"
              className={errors.lastName ? 'error' : ''}
            />
            {errors.lastName && <div className="error-message">{errors.lastName}</div>}
          </FormGroup>
        </FieldRow>
        
        <FieldRow>
          <FormGroup>
            <label htmlFor="email">Email*</label>
            <Input 
              type="email" 
              id="email" 
              name="email" 
              value={formData.email} 
              onChange={handleChange}
              placeholder="john@example.com"
              className={errors.email ? 'error' : ''}
            />
            {errors.email && <div className="error-message">{errors.email}</div>}
          </FormGroup>
          
          <FormGroup>
            <label htmlFor="phone">Phone*</label>
            <Input 
              type="tel" 
              id="phone" 
              name="phone" 
              value={formData.phone} 
              onChange={handleChange}
              placeholder="+973 XXXX XXXX"
              className={errors.phone ? 'error' : ''}
            />
            {errors.phone && <div className="error-message">{errors.phone}</div>}
          </FormGroup>
        </FieldRow>
      </FormSection>
      
      <FormSection>
        <SectionTitle>Delivery Information</SectionTitle>
        <FormGroup>
          <label htmlFor="address">Address*</label>
          <TextArea 
            id="address" 
            name="address" 
            value={formData.address} 
            onChange={handleChange}
            placeholder="Enter your full address"
            className={errors.address ? 'error' : ''}
          />
          {errors.address && <div className="error-message">{errors.address}</div>}
        </FormGroup>
        
        <FieldRow>
          <FormGroup>
            <label htmlFor="city">City*</label>
            <Select 
              id="city" 
              name="city" 
              value={formData.city} 
              onChange={handleChange}
              className={errors.city ? 'error' : ''}
            >
              <option value="">Select City</option>
              {BAHRAIN_CITIES.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </Select>
            {errors.city && <div className="error-message">{errors.city}</div>}
          </FormGroup>
          
          <FormGroup>
            <label htmlFor="postalCode">Postal Code*</label>
            <Input 
              type="text" 
              id="postalCode" 
              name="postalCode" 
              value={formData.postalCode} 
              onChange={handleChange}
              placeholder="Enter postal code"
              className={errors.postalCode ? 'error' : ''}
            />
            {errors.postalCode && <div className="error-message">{errors.postalCode}</div>}
          </FormGroup>
        </FieldRow>
      </FormSection>
      
      <FormSection>
        <SectionTitle>Payment Method</SectionTitle>
        <PaymentMethodsContainer>
          {PAYMENT_METHODS.map(method => (
            <PaymentMethod 
              key={method.id} 
              className={formData.paymentMethod === method.id ? 'selected' : ''}
            >
              <input 
                type="radio" 
                id={method.id} 
                name="paymentMethod" 
                value={method.id} 
                checked={formData.paymentMethod === method.id}
                onChange={handleChange}
              />
              {method.name}
            </PaymentMethod>
          ))}
        </PaymentMethodsContainer>
      </FormSection>
      
      <FormSection>
        <SectionTitle>Additional Information</SectionTitle>
        <FormGroup>
          <label htmlFor="notes">Order Notes (Optional)</label>
          <TextArea 
            id="notes" 
            name="notes" 
            value={formData.notes} 
            onChange={handleChange}
            placeholder="Special notes for delivery"
          />
        </FormGroup>
        
        <TermsCheckbox>
          <input 
            type="checkbox" 
            id="agreeToTerms" 
            name="agreeToTerms" 
            checked={formData.agreeToTerms}
            onChange={handleCheckboxChange}
          />
          <label htmlFor="agreeToTerms">
            I agree to the <a href="/terms" target="_blank">Terms and Conditions</a> and <a href="/privacy" target="_blank">Privacy Policy</a>
          </label>
        </TermsCheckbox>
        {errors.agreeToTerms && <div className="error-message">{errors.agreeToTerms}</div>}
      </FormSection>
      
      <SubmitButton type="submit">
        <FontAwesomeIcon icon={faLock} /> Place Order
      </SubmitButton>
    </Form>
  );
};

export default CheckoutForm; 